import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Customer, Workstation } from '@visionqc/shared';
import { useIsAdmin } from '../api/auth';
import { api } from '../api/client';
import { procguardApi, type ProcguardAnalytics } from '../api/procguard';

const WINDOWS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const DEV_LABEL: Record<string, string> = {
  missing_component: 'Missing component', wrong_component: 'Wrong component', misaligned_component: 'Misaligned',
  step_skipped: 'Step skipped', wrong_order: 'Wrong order', step_overtime: 'Step overtime',
  cycle_overtime: 'Cycle overtime', kit_incomplete: 'Kit incomplete',
};

/** Plant-facing analytics: compliance, first-time-right, deviations, cycle time, kitting. */
export function ProcGuardDashboardView() {
  const isAdmin = useIsAdmin();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [workstationId, setWorkstationId] = useState<number | 'all'>('all');
  const [window_, setWindow] = useState('7d');
  const [a, setA] = useState<ProcguardAnalytics | null>(null);

  useEffect(() => {
    api.listCustomers().then((cs) => { setCustomers(cs); setCustomerId((id) => id ?? cs[0]?.id ?? null); }).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (customerId) procguardApi.listWorkstations(customerId).then(setWorkstations).catch(() => undefined);
  }, [customerId]);

  const load = useCallback(() => {
    if (!customerId) return;
    procguardApi.analytics({
      customerId: workstationId === 'all' ? customerId : undefined,
      workstationId: workstationId === 'all' ? undefined : workstationId,
      window: window_,
    }).then(setA).catch(() => setA(null));
  }, [customerId, workstationId, window_]);
  useEffect(() => load(), [load]);

  if (!isAdmin) return <div className="panel muted">The procguard dashboard requires the ADMIN role.</div>;

  const maxDev = Math.max(1, ...(a?.topDeviations.map((d) => d.count) ?? [1]));
  const maxBucket = Math.max(1, ...(a?.timeseries.map((b) => b.ok + b.nok + b.aborted) ?? [1]));

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Jakatori — analytics</h1>
        <div className="row" style={{ gap: 8 }}>
          <Link className="btn sm" to="/procguard">← Workstations</Link>
          <Link className="btn sm" to="/procguard/cycles">Cycles</Link>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div><label>Customer</label>
            <select value={customerId ?? ''} onChange={(e) => { setCustomerId(Number(e.target.value)); setWorkstationId('all'); }}>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label>Workstation</label>
            <select value={String(workstationId)} onChange={(e) => setWorkstationId(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
              <option value="all">All workstations</option>
              {workstations.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div><label>Window</label>
            <select value={window_} onChange={(e) => setWindow(e.target.value)}>{WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}</select>
          </div>
        </div>
      </div>

      {!a ? (
        <div className="panel spinner">Loading…</div>
      ) : a.totals.cycles === 0 ? (
        <div className="panel muted">No cycles in this window yet.</div>
      ) : (
        <>
          <div className="row" style={{ gap: 16, alignItems: 'stretch', flexWrap: 'wrap' }}>
            <Tile label="Process compliance" value={`${a.compliancePct}%`} tone="var(--good)" />
            <Tile label="First-time-right" value={`${a.firstTimeRightPct}%`} tone="var(--accent)" />
            <Tile label="Deviation rate" value={a.deviationRate.toFixed(2)} tone="var(--warn)" sub="per cycle" />
            <Tile label="Avg cycle time" value={a.avgDurationMs != null ? `${(a.avgDurationMs / 1000).toFixed(1)}s` : '—'} tone="var(--teal)" />
            <Tile label="Kitting discrepancy" value={`${a.kitting.discrepancyPct}%`} tone="var(--bad)" sub={`${a.kitting.kitsWithDiscrepancy}/${a.kitting.kitsVerified} kits`} />
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 16, marginTop: 16 }}>
            <div className="panel">
              <strong>Cycles over time</strong>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 160, marginTop: 12 }}>
                {a.timeseries.map((b) => {
                  const tot = b.ok + b.nok + b.aborted;
                  const h = (n: number) => `${(n / maxBucket) * 140}px`;
                  return (
                    <div key={b.t} title={`${new Date(b.t).toLocaleDateString()} · ${tot} cycles`} style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', minWidth: 6 }}>
                      <div style={{ height: h(b.ok), background: 'var(--good)' }} />
                      <div style={{ height: h(b.nok), background: 'var(--bad)' }} />
                      <div style={{ height: h(b.aborted), background: 'var(--warn)' }} />
                    </div>
                  );
                })}
              </div>
              <div className="row muted" style={{ gap: 14, marginTop: 8, fontSize: 12 }}>
                <span><span style={{ color: 'var(--good)' }}>■</span> OK</span>
                <span><span style={{ color: 'var(--bad)' }}>■</span> NOK</span>
                <span><span style={{ color: 'var(--warn)' }}>■</span> Aborted</span>
              </div>
            </div>

            <div className="panel">
              <strong>Top deviations</strong>
              {a.topDeviations.length === 0 ? <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>None</div> : (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {a.topDeviations.map((d) => (
                    <div key={d.kind}>
                      <div className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}><span>{DEV_LABEL[d.kind] ?? d.kind}</span><span>{d.count}</span></div>
                      <div style={{ height: 6, background: 'var(--bg-panel-2)', borderRadius: 3, marginTop: 3 }}>
                        <div style={{ width: `${(d.count / maxDev) * 100}%`, height: '100%', background: 'var(--warn)', borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16, marginTop: 16 }}>
            <div className="panel">
              <strong>Deviations per step</strong>
              {a.perStep.length === 0 ? <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>None.</div> : (
                <table style={{ width: '100%', marginTop: 8 }}><tbody>
                  {a.perStep.map((s) => <tr key={s.seq}><td>{s.seq}. {s.name}</td><td style={{ textAlign: 'right', color: 'var(--warn)' }}>{s.deviations}</td></tr>)}
                </tbody></table>
              )}
            </div>
            <div className="panel">
              <strong>Per operator</strong>
              <table style={{ width: '100%', marginTop: 8 }}>
                <thead><tr><th>Operator</th><th>Cycles</th><th>FTR</th><th>Avg</th></tr></thead>
                <tbody>
                  {a.perOperator.map((o) => (
                    <tr key={o.operator}>
                      <td>{o.operator}</td><td>{o.cycles}</td>
                      <td>{o.cycles ? Math.round((o.firstTimeRight / o.cycles) * 100) : 0}%</td>
                      <td className="muted">{o.avgDurationMs != null ? `${(o.avgDurationMs / 1000).toFixed(1)}s` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, tone, sub }: { label: string; value: string; tone: string; sub?: string }) {
  return (
    <div className="stat-tile" style={{ flex: '1 1 150px', ['--tone' as string]: tone }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}{sub ? ` · ${sub}` : ''}</div>
    </div>
  );
}
